// ─────────────────────────────────────────────────────────────────────────────
// Shared Buy Box / underwriting setup-completeness field lists.
//
// These are the canonical required-field lists the server scan gate enforces
// (checkSetupCompleteness in server/routes/buy-box.ts). They live here so the
// client setup-completeness checklist (Buy Box settings + Underwriting defaults
// pages) stays in lockstep with the gate instead of drifting from a hand-copied
// list.
//
// Fill semantics mirror the gate exactly:
//   - Buy Box criteria fields: a field is filled iff its value is not null.
//     (an explicit 0 / false counts as filled.)
//   - Underwriting text fields: filled iff not null AND not "".
//   - Underwriting int fields:  filled iff not null. (0 counts as filled.)
// ─────────────────────────────────────────────────────────────────────────────

export interface SetupField {
  key: string;
  label: string;
}

// Required Buy Box criteria fields — every criterion the scan engine filters on.
export const BUY_BOX_REQUIRED_CRITERIA_FIELDS: readonly SetupField[] = [
  { key: "minCocReturn", label: "Min Cash-on-Cash Return" },
  { key: "minMonthlyCashFlow", label: "Min Monthly Cash Flow" },
  { key: "maxPurchasePrice", label: "Max Purchase Price" },
  { key: "minBedrooms", label: "Min Converted Bedrooms" },
  { key: "maxBedrooms", label: "Max Converted Bedrooms" },
  { key: "minSqft", label: "Min Square Footage" },
  { key: "maxSqft", label: "Max Square Footage" },
  { key: "noHoa", label: "No HOA preference" },
] as const;

// Required underwriting-defaults fields. Kept in the {textFields, intFields}
// shape the server gate consumes; server/underwriting-engine.ts re-exports this
// as UNDERWRITING_DEFAULTS_REQUIRED_FIELDS so there is a single source of truth.
export const UNDERWRITING_DEFAULTS_REQUIRED_FIELDS = {
  textFields: [
    "downpaymentPercent",
    "closingCostsPercent",
    "furnishingCostPerRoom",
    "annualInterestRate",
    "vacancyPercent",
    "pmFee",
    "sewerWater",
    "gas",
    "electricityPerRoom",
    "internet",
    "lawnCare",
    "cleaning",
    "capitalImprovementsBudget",
    "miscRepairs",
    "hoa",
    "annualPropertyTaxPercent",
    "annualInsurancePercent",
    "roomAdditionsCost",
    "bathAdditionsCost",
    "kitchenAmount",
    "hvacAmount",
    "roofAmount",
    "plumbingAmount",
    "foundationRepairAmount",
    "otherAmount",
    "additionalRehabAmount",
    "bufferPercent",
  ] as const,
  intFields: [
    "monthsUntilOccupied",
  ] as const,
} as const;

// Human-friendly labels for the underwriting-defaults checklist. Mirrors the
// field labels on the Underwriting Defaults page.
const UNDERWRITING_FIELD_LABELS: Record<string, string> = {
  downpaymentPercent: "Downpayment %",
  closingCostsPercent: "Closing Cost %",
  furnishingCostPerRoom: "Furnishing Cost Per Room",
  annualInterestRate: "Annual Interest Rate",
  vacancyPercent: "Vacancy %",
  pmFee: "PM Fee",
  sewerWater: "Sewer & Water",
  gas: "Gas",
  electricityPerRoom: "Electricity Per Room",
  internet: "Internet",
  lawnCare: "Lawn Care",
  cleaning: "Cleaning",
  capitalImprovementsBudget: "Capital Improvements Budget",
  miscRepairs: "Misc Repairs",
  hoa: "HOA",
  annualPropertyTaxPercent: "Annual Property Tax % of PP",
  annualInsurancePercent: "Annual Insurance % of PP",
  roomAdditionsCost: "Room Addition Cost",
  bathAdditionsCost: "Bath Addition Cost",
  kitchenAmount: "Kitchen Amount",
  hvacAmount: "HVAC Amount",
  roofAmount: "Roof Amount",
  plumbingAmount: "Plumbing Amount",
  foundationRepairAmount: "Foundation Repair Amount",
  otherAmount: "Other Amount",
  additionalRehabAmount: "Additional Rehab % of PP",
  bufferPercent: "Buffer %",
  monthsUntilOccupied: "Months Until Occupied",
};

// Flat labeled list of every required underwriting-defaults field, in display order.
export const UNDERWRITING_REQUIRED_FIELDS: readonly SetupField[] = [
  ...UNDERWRITING_DEFAULTS_REQUIRED_FIELDS.textFields,
  ...UNDERWRITING_DEFAULTS_REQUIRED_FIELDS.intFields,
].map((key) => ({ key, label: UNDERWRITING_FIELD_LABELS[key] ?? key }));

function isCriteriaFieldFilled(value: unknown): boolean {
  return value != null;
}

function isUnderwritingFieldFilled(key: string, value: unknown): boolean {
  const isIntField = (UNDERWRITING_DEFAULTS_REQUIRED_FIELDS.intFields as readonly string[]).includes(key);
  if (isIntField) return value != null;
  return value != null && value !== "";
}

// Returns the subset of Buy Box criteria fields that are still blank.
export function getMissingBuyBoxFields(criteria: Record<string, unknown> | null | undefined): SetupField[] {
  if (!criteria) return [...BUY_BOX_REQUIRED_CRITERIA_FIELDS];
  return BUY_BOX_REQUIRED_CRITERIA_FIELDS.filter((f) => !isCriteriaFieldFilled(criteria[f.key]));
}

// Returns the subset of underwriting-defaults fields that are still unset.
export function getMissingUnderwritingFields(defaults: Record<string, unknown> | null | undefined): SetupField[] {
  if (!defaults) return [...UNDERWRITING_REQUIRED_FIELDS];
  return UNDERWRITING_REQUIRED_FIELDS.filter((f) => !isUnderwritingFieldFilled(f.key, defaults[f.key]));
}
